import type { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faBullhorn } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { CSSProperties, JSX } from 'react';
import React from 'react';

import type { Campaign, CampaignClass } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { EFontAwesomeKitIcon, FontAwesomeKitIcon } from '@feathr/components';
import {
  campaignColorMap,
  campaignIconMap,
  customCampaignIconMap,
} from '@feathr/extender/styles/campaign';
import { cssVar } from '@feathr/hooks';

import { skeleton } from '@feathr/components/dist/style/skeleton.css';
import styles from './CampaignIcon.css';

interface IBaseProps {
  className?: string;
  disabled?: boolean;
  size: 36 | 48;
  hover?: boolean;
}

interface ICampaignProps extends IBaseProps {
  cls?: never;
  campaign: Campaign;
}

interface IClassProps extends IBaseProps {
  cls: CampaignClass;
  campaign?: never;
}

// TODO: Eventually move to utilizing <Icon /> when it's spacing issues have been fixed
const sizeMap: Record<number, SizeProp> = {
  36: '1x',
  48: 'lg',
};

function getStyle({
  disabled,
  backgroundColor,
  size,
}: {
  disabled?: boolean;
  backgroundColor?: string | undefined;
  size: 36 | 48;
}): CSSProperties {
  function getBackgroundColor(): string | null {
    if (disabled) {
      return cssVar('--color-bg-quaternary');
    }
    if (backgroundColor) {
      return cssVar(`--color-${backgroundColor}-500`);
    }
    return null;
  }

  function getCircleSize(): string {
    return `${size}px`;
  }

  function getIconColor(): string {
    if (disabled) {
      return cssVar('--color-disabled');
    }
    return cssVar('--color-text-body-inverted');
  }

  return {
    ['--l-icon-background-color' as string]: getBackgroundColor(),
    ['--l-circle-size' as string]: getCircleSize(),
    ['--l-icon-color' as string]: getIconColor(),
  };
}

export function getCampaignIcon(
  campaignClass: CampaignClass,
  size?: SizeProp,
  className?: string,
): JSX.Element | null {
  if (customCampaignIconMap.has(campaignClass)) {
    return (
      <FontAwesomeKitIcon
        className={className}
        data-name={'campaign-type-icon'}
        iconName={customCampaignIconMap.get(campaignClass) ?? EFontAwesomeKitIcon.feathr}
        size={size}
      />
    );
  }
  return (
    <FontAwesomeIcon
      className={className}
      data-name={'campaign-type-icon'}
      icon={campaignIconMap.get(campaignClass) ?? faBullhorn}
      size={size}
    />
  );
}

function CampaignIcon({
  campaign,
  className,
  cls,
  disabled = false,
  size,
  hover = false,
}: ICampaignProps | IClassProps): JSX.Element {
  const isPending = (campaign && campaign.isPending) || false;
  const isDraft = (campaign && campaign.get('state') === CampaignState.Draft) || false;

  const backgroundColor = campaignColorMap.get(campaign ? campaign.get('_cls') : cls!);

  return (
    <div
      className={classNames(
        styles.root,
        { [styles.hoverable]: hover },
        { [styles.draft]: isDraft },
        { [skeleton]: isPending },
        className,
      )}
      style={getStyle({ disabled, backgroundColor, size })}
    >
      {!isPending && getCampaignIcon(cls || campaign.get('_cls'), sizeMap[size])}
    </div>
  );
}

export default CampaignIcon;
